import { useState } from "react";
import { Heart, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDonorOverview, useAgreementCharges } from "@/hooks/useDonors";
import {
  formatNok,
  formatDate,
  AGREEMENT_LABEL,
  CHARGE_LABEL,
  type AgreementStatus,
  type DonorRow,
} from "@/lib/donors";

const STATUS_FILTER = ["all", "ACTIVE", "PENDING", "STOPPED", "EXPIRED"] as const;
type StatusFilter = (typeof STATUS_FILTER)[number];

function statusBadge(status: AgreementStatus) {
  const label = AGREEMENT_LABEL[status];
  switch (status) {
    case "ACTIVE":
      return <Badge className="bg-green-500/15 text-green-700 hover:bg-green-500/20">{label}</Badge>;
    case "PENDING":
      return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20">{label}</Badge>;
    case "STOPPED":
    case "EXPIRED":
      return <Badge variant="secondary">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

const DonorChargesPanel = ({ donor }: { donor: DonorRow }) => {
  const { data: charges, isLoading } = useAgreementCharges(donor.id);
  if (isLoading) return <p className="text-sm text-muted-foreground">Laster trekk …</p>;
  const items = charges ?? [];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border bg-card p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Totalt trukket</p>
          <p className="mt-1 text-xl font-semibold">{formatNok(donor.total_charged)}</p>
        </div>
        <div className="rounded-md border bg-card p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Neste trekk</p>
          <p className="mt-1 text-xl font-semibold">{formatDate(donor.next_charge_at)}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ingen trekk registrert ennå.</p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left">
              <tr>
                <th className="px-3 py-2 font-medium text-muted-foreground">Dato</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Beløp</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Status</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Merknad</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {c.charged_at ? formatDate(c.charged_at) : formatDate(c.due_at)}
                  </td>
                  <td className="px-3 py-2">{formatNok(c.amount_nok)}</td>
                  <td className="px-3 py-2">{CHARGE_LABEL[c.status]}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{c.failure_reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Kpi = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}) => (
  <div className="rounded-lg border bg-card p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="mt-2 text-2xl font-semibold">{value}</p>
  </div>
);

const AdminDonors = () => {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [openDonor, setOpenDonor] = useState<DonorRow | null>(null);
  const { data, isLoading, error } = useDonorOverview();

  const filtered =
    filter === "all"
      ? data?.donors ?? []
      : (data?.donors ?? []).filter((d) => d.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Fastgivere</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Oversikt over Vipps Recurring-avtaler. Synkroniseres automatisk via webhook fra Vipps.
          </p>
        </div>
        <div className="w-48">
          <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="ACTIVE">Aktive</SelectItem>
              <SelectItem value="PENDING">Venter</SelectItem>
              <SelectItem value="STOPPED">Stoppet</SelectItem>
              <SelectItem value="EXPIRED">Utløpt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Kpi label="Aktive avtaler" value={data.active_count.toString()} icon={Heart} />
          <Kpi label="Månedlig sum" value={formatNok(data.monthly_sum)} icon={Heart} />
          <Kpi label="Totalt trukket" value={formatNok(data.total_charged)} icon={Heart} />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster …</p>
      ) : error ? (
        <p className="text-sm text-destructive">Kunne ikke laste fastgivere.</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          Ingen fastgivere å vise.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Kontakt</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Beløp</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Neste trekk</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Totalt trukket</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Feilet</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  className="cursor-pointer hover:bg-muted/20"
                  onClick={() => setOpenDonor(d)}
                >
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium">{d.email ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{d.phone_number ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatNok(d.amount_nok)}</td>
                  <td className="px-4 py-3">{statusBadge(d.status)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(d.next_charge_at)}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatNok(d.total_charged)}</td>
                  <td className="px-4 py-3 text-sm">
                    {d.failed_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-destructive">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {d.failed_count}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-medium text-primary">Detaljer</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(openDonor)} onOpenChange={(open) => !open && setOpenDonor(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{openDonor?.email ?? "Fastgiver"}</DialogTitle>
            <DialogDescription>
              Avtale-id: <span className="font-mono">{openDonor?.vipps_agreement_id}</span>
            </DialogDescription>
          </DialogHeader>
          {openDonor && <DonorChargesPanel donor={openDonor} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDonors;
